import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMainImage } from '../../../utils/image-utils';
import { getApiEndpoint } from '../../shared/utils/api-url';

interface Category {
  id: number;
  name: string;
  icon?: string;
  image?: string;
}

interface Product {
  id: number;
  name: string;
  price: number;
  original_price?: number;
  images?: { main: string; colors?: { [color: string]: string } };
  featured?: boolean;
}

function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategories();
    loadFeaturedProducts();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await fetch(getApiEndpoint('/categories?active=true'));
      if (!response.ok) throw new Error('Erro ao carregar categorias');
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadFeaturedProducts = async () => {
    try {
      const response = await fetch(getApiEndpoint('/products?active=true&featured=true'));
      if (!response.ok) throw new Error('Erro ao carregar produtos');
      const data = await response.json();
      // Parse images se for string
      const parsedData = data.map((product: any) => {
        if (typeof product.images === 'string') {
          try {
            product.images = JSON.parse(product.images);
          } catch (e) {
            product.images = { main: product.images || '' };
          }
        }
        return product;
      });
      setProducts(parsedData);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  const banners = [
    { id: 1, color: '#3498db', text: 'Banner 1' },
    { id: 2, color: '#e74c3c', text: 'Banner 2' },
    { id: 3, color: '#27ae60', text: 'Banner 3' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [banners.length]);

  return (
    <>
      {/* Banner Carousel */}
      <section className="banner-section">
        <div className="banner-carousel" id="banner-carousel">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`banner-slide ${index === currentSlide ? 'active' : ''}`}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background: banner.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: 600,
                }}
              >
                {banner.text}
              </div>
            </div>
          ))}
        </div>
        <div className="banner-dots" id="banner-dots">
          {banners.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
            ></button>
          ))}
        </div>
      </section>

      {/* Categories Scroll */}
      <section className="categories-section">
        <h2 className="section-title">Categorias</h2>
        <div className="categories-scroll" id="categories-scroll">
          {categories.length === 0 ? (
            <div className="empty-message" style={{ padding: '20px', textAlign: 'center' }}>
              Nenhuma categoria disponível
            </div>
          ) : (
            categories.map((category) => (
              <Link key={category.id} to={`/catalogo?category=${category.id}`} className="category-item">
                {category.image ? (
                  <img src={category.image} alt={category.name} className="category-image" />
                ) : category.icon ? (
                  <div className="category-icon">
                    <i className={`fas fa-${category.icon}`}></i>
                  </div>
                ) : (
                  <div className="category-icon">
                    <i className="fas fa-tag"></i>
                  </div>
                )}
                <span className="category-name">{category.name}</span>
              </Link>
            ))
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="products-section">
        <div className="section-header">
          <h2 className="section-title">Destaques</h2>
          <Link to="/catalogo" className="see-all">Ver todos</Link>
        </div>
        {loading ? (
          <div className="loading-message">Carregando produtos...</div>
        ) : products.length === 0 ? (
          <div className="empty-message">Nenhum produto em destaque no momento</div>
        ) : (
          <div className="products-grid" id="featured-products">
            {products.map((product) => {
              const mainImage = getMainImage(product.images);
              const discount = product.original_price && product.price < product.original_price
                ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
                : null;

              return (
                <Link key={product.id} to={`/produto/${product.id}`} className="product-card">
                  {discount && (
                    <div className="product-badge">-{discount}%</div>
                  )}
                  <img
                    src={mainImage}
                    alt={product.name}
                    className="product-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle' font-family='Arial, sans-serif' font-size='12' fill='%23999'%3EProduto%3C/text%3E%3C/svg%3E`;
                    }}
                  />
                  <div className="product-info">
                    <div className="product-name">{product.name}</div>
                    <div className="product-price">
                      <span className="current-price">R$ {product.price.toFixed(2).replace('.', ',')}</span>
                      {product.original_price && product.original_price > product.price && (
                        <span className="old-price">R$ {product.original_price.toFixed(2).replace('.', ',')}</span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}

export default Home;

